<template>
  <a-col
    :id="'trace-page-video-single' + index"
    :xs="24"
    :sm="24"
    :md="24"
    :lg="12"
    :xl="12"
  >
    <div class="cycle-card">
      <div
        class="cycle-thumb"
        :class="{ 'cycle-thumb--missing': !video.isThumbnailExist }"
      >
        <img
          v-if="video.isThumbnailExist"
          :id="'trace-page-video-' + index + '-thumbnail'"
          class="clickable video-img"
          :src="thumbnailImage"
          :alt="getFileName(video.fileName)"
          @click="handleVideoPlay"
          @error="handleError(video)"
        />

        <NotFoundImage
          v-else
          class="cycle-thumb__missing-img"
          :alt-url="thumbnailImage"
        />
        <!-- Decorative only: clicks pass through to the thumbnail -->
        <span v-if="video.isThumbnailExist" class="cycle-thumb__play">
          <CaretRightFilled />
        </span>
        <span v-if="video?.cycle_time" class="cycle-thumb__duration">
          {{ video.cycle_time }}
        </span>
      </div>

      <div class="cycle-card__body">
        <div class="cycle-card__head">
          <span
            class="cycle-card__title"
            :title="formatDate(video?.cycle_identifier)"
          >
            {{ formatDate(video?.cycle_identifier) }}
          </span>
          <a-tag
            :key="video.is_good_cycle"
            class="cycle-card__status"
            :color="getCycleTagColor"
          >
            {{ getCycleTagText }}
          </a-tag>
          <a-space class="ml-auto cycle-card__head-actions" :size="2">
            <a-tooltip title="Bookmark this video">
              <span class="cycle-card__bookmark">
                <a-rate
                  :value="Number(video.starred)"
                  :count="1"
                  @change="handleBookmark"
                />
              </span>
            </a-tooltip>
            <a-tooltip title="Copy Video Link">
              <a-button size="small" type="text" @click="handleCopyUrl(video)">
                <template #icon>
                  <i class="bi bi-copy" />
                </template>
              </a-button>
            </a-tooltip>
            <a-tooltip title="Playback trace video in new tab">
              <a-button size="small" type="text" @click="goToTraceTab(video)">
                <template #icon>
                  <i class="bi bi-box-arrow-up-right" />
                </template>
              </a-button>
            </a-tooltip>
          </a-space>
        </div>

        <div class="cycle-card__meta">
          <div class="cycle-meta">
            <div class="cycle-meta__label">Cycle Time</div>
            <div class="cycle-meta__value">{{ video?.cycle_time }}</div>
          </div>
          <div class="cycle-meta">
            <div class="cycle-meta__label">Missed Steps</div>
            <div
              class="cycle-meta__value"
              :class="
                missedStepsCount > 0
                  ? 'cycle-meta__value--alert'
                  : 'cycle-meta__value--quiet'
              "
            >
              {{ missedStepsCount }}
            </div>
          </div>
        </div>

        <div
          :id="isInterestingCycles ? 'area1-' + index : 'area2-' + index"
          class="cycle-card__toolbar"
        >
          <a-dropdown
            trigger="click"
            placement="bottom"
            :get-popup-container="getPopupContainer"
          >
            <a-button size="small" type="link" class="cycle-card__missed-btn">
              Show Missed Steps
            </a-button>

            <template #overlay>
              <a-menu>
                <a-menu-item
                  v-if="!video.is_good_cycle && video?.missedStepsName.length"
                  v-for="ms in video?.missedStepsName"
                  :key="ms"
                >
                  {{ ms }}
                </a-menu-item>
                <a-menu-item v-else> No missed steps </a-menu-item>
              </a-menu>
            </template>
          </a-dropdown>

          <!-- Validate and archive are shown to support users only -->
          <a-space class="ml-auto" :size="2" wrap>
            <a-tooltip title="Show comments">
              <a-badge
                :count="video.no_of_comments"
                :overflow-count="10"
                size="small"
                color="#1890ff"
              >
                <a-button
                  size="small"
                  type="text"
                  @click="$emit('showComments', true, index)"
                >
                  <template #icon>
                    <CommentOutlined />
                  </template>
                </a-button>
              </a-badge>
            </a-tooltip>

            <a-tooltip title="Show tags">
              <a-badge
                :count="tagsCount"
                :overflow-count="10"
                size="small"
                color="#1890ff"
              >
                <a-button size="small" type="text" @click="openTagModal">
                  <template #icon>
                    <TagsOutlined />
                  </template>
                </a-button>
              </a-badge>
            </a-tooltip>

            <a-popconfirm
              :title="`Are you sure you want to mark this as a ${
                video?.is_good_cycle == 0 ? 'Good' : 'Bad'
              } cycle?`"
              ok-text="Yes"
              cancel-text="No"
              @confirm="toggleCycleStatus()"
            >
              <a-tooltip
                :title="`Mark as ${
                  video?.is_good_cycle === 0 ? 'Good' : 'Bad'
                }`"
              >
                <a-button size="small" type="text">
                  <template #icon>
                    <ToolOutlined />
                  </template>
                </a-button>
              </a-tooltip>
            </a-popconfirm>

            <a-tooltip title="Show Line History">
              <a-button size="small" type="text" @click="openLineVisibility">
                <template #icon>
                  <HistoryOutlined />
                </template>
              </a-button>
            </a-tooltip>

            <a-tooltip title="Label Video">
              <a-button
                size="small"
                type="text"
                @click="handleClickLabellingTool"
              >
                <template #icon>
                  <EditOutlined />
                </template>
              </a-button>
            </a-tooltip>

            <a-tooltip
              v-if="role == roles.support_user"
              title="Validated Video"
            >
              <a-button size="small" type="text">
                <template #icon>
                  <CheckCircleOutlined @click="validateVideoModal(video)" />
                </template>
              </a-button>
            </a-tooltip>

            <span
              v-if="role == roles.support_user"
              class="cycle-card__toolbar-sep"
            />

            <a-tooltip v-if="role == roles.support_user" title="Archive cycle">
              <a-popconfirm
                title="Are you sure you want to archive this cycle?"
                ok-text="Yes"
                cancel-text="No"
                @confirm="archiveCycle(video)"
              >
                <a-button size="small" type="text" danger>
                  <template #icon>
                    <DeleteOutlined />
                  </template>
                </a-button>
              </a-popconfirm>
            </a-tooltip>
          </a-space>
        </div>
      </div>
    </div>
  </a-col>
  <a-modal
    id="assign-group-modal"
    v-model:visible="showAssignGroupModal"
    centered
    :title="selectedFile?.fileName"
    :body-style="{
      height: '15vh',
    }"
    @ok="validateVideo"
  >
    <a-row>
      <a-col :span="24">
        <a-select
          v-model:value="selectedValidateGroup"
          placeholder="Select Group"
          style="width: 100%"
          :options="validateGroupVideos"
        />
      </a-col>
    </a-row>
  </a-modal>

  <a-modal
    id="tags-modal"
    v-model:visible="tagsModal"
    title="Tags"
    :footer="null"
    centered
  >
    <template #closeIcon>
      <close-outlined id="tags-modal-close-btn" />
    </template>
    <div style="height: 30vh">
      <a-list
        class="tags-list"
        size="small"
        item-layout="horizontal"
        :data-source="tags"
        style="flex-grow: 1; max-height: 30vh; overflow-y: auto"
      >
        <template #renderItem="{ item }">
          <a-list-item v-if="item?.tag">
            <a-list-item-meta
              :title="`${item.tag.name} - ${item.step_name}`"
              :description="`${item.added_by}`"
            />
            <div>{{ item.time_stamp }}</div>
          </a-list-item>
        </template>
      </a-list>
    </div>
  </a-modal>
</template>
<script>
import {
  CaretRightFilled,
  CheckCircleFilled,
  CheckCircleOutlined,
  CloseOutlined,
  CommentOutlined,
  DeleteOutlined,
  EditOutlined,
  HistoryOutlined,
  TagsOutlined,
  ToolOutlined,
} from '@ant-design/icons-vue';
import NotFoundImage from 'src/components/shared/Components/NotFound.vue';
import { roles } from 'src/config/roles-config';
import httpClient from 'src/service/httpClient.js';
import ThumbnailService from 'src/services/thumbnails';
import { getSortedTask } from 'src/utils/task';
import { getThumbnailUrl, traceThumbnail } from 'src/utils/thumbnail.js';
import { mapActions, mapGetters } from 'vuex';
import TelemetryService from '../../../../services/telemetry';
import VideoService from '../../../../services/videos';

export default {
  components: {
    ToolOutlined,
    DeleteOutlined,
    TagsOutlined,
    CommentOutlined,
    NotFoundImage,
    CloseOutlined,
    HistoryOutlined,
    CheckCircleOutlined,
    CheckCircleFilled,
    EditOutlined,
    CaretRightFilled,
  },
  inject: ['toast'],
  props: [
    'video',
    'cycle',
    'index',
    'isInterestingCycles',
    'goToTraceTab',
    'validateGroupVideos',
    'orgTags',
  ],
  emits: [
    'updateVideoUrl',
    'handlePlay',
    'showComments',
    'updateVideo',
    'toggleCycleStatus',
    'archivedCycleCount',
    'bookMarkVideo',
    'handleCopyUrl',
    'openLabellingTool',
  ],
  setup() {
    const userId = localStorage.getItem('id');
    const userName = localStorage.getItem('name');
    const organization = localStorage.getItem('organization');
    return { userId, userName, organization };
  },
  data: function () {
    return {
      selectedFile: null,
      selectedValidateGroup: [],
      showAssignGroupModal: false,
      tagOptions: [],
      playable: false,
      document: document,
      queuedThumbnailGenerations: [],
      isThumbnailExist: true,
      thumbnailImage: null,
      roles,
      tagsModal: false,
      isLoadingTag: false,
      currentSelectedTag: [],
      orgTags: [],
      loadingTags: false,
      selectedVideo: null,
      tags: [],
      tagsCount: 0,
      // validateGroupVideos: [],
    };
  },
  computed: {
    ...mapGetters(['selectedTask', 'allTasks', 'organization', 'role']),
    getCycleTagColor() {
      return !this.video.is_good_cycle ? 'error' : 'success';
    },

    getCycleTagText() {
      return !this.video.is_good_cycle ? 'Bad' : 'Good';
    },

    missedStepsCount() {
      return this.video.is_good_cycle ? 0 : this.video?.missedStepsName?.length;
    },
  },
  watch: {
    // selectedTask: {
    //   handler: async function (value) {
    //     this.validateGroupVideos = (
    //       await this.groupValidateVideos({ task: value })
    //     ).map((data) => ({
    //       value: data.id,
    //       label: data.name,
    //     }));
    //   },
    //   deep: true,
    // },
    video: {
      handler: function (value) {
        this.thumbnailImage = this.thumbnailUrl(value);
        if (
          !value.isThumbnailExist &&
          !this.isThumbnailGenerationInitiated(value)
        ) {
          this.queuedThumbnailGenerations.push(value.id);
          this.handleThumbnailGeneration(value);
        }
      },
      deep: true,
    },
  },

  async mounted() {
    // this.validateGroupVideos = (
    //   await this.groupValidateVideos({ task: this.selectedTask })
    // ).map((data) => ({
    //   value: data.id,
    //   label: data.name,
    // }));
    this.thumbnailImage = this.thumbnailUrl(this.video);
    this.fetchTags(this.video, false);
  },

  methods: {
    ...mapActions([
      'setNewTags',
      'changeRoute',
      'fetchTaskDetails',
      'groupValidateVideos',
    ]),

    openLineVisibility() {
      this.changeRoute(['Line Visibility']);
      this.$router.push({
        path: '/user/line-visibility',
        query: {
          taskId: this.selectedTask,
          cycleId: this.video.cycle_id,
        },
      });
    },

    handleClickLabellingTool() {
      this.$emit('openLabellingTool', true, this.video);
    },

    formatDate(value) {
      if (!value) return '';

      const date = new Date(value);
      return date.toLocaleString('sv-SE', {
        hour12: false,
        timeZone: 'UTC',
      });
    },
    async toggleCycleStatus() {
      this.$emit('toggleCycleStatus', {
        video: this.video,
        isGoodCycle: !this.video.is_good_cycle,
      });
    },

    filterOption(input, option) {
      return option.label?.toLowerCase().indexOf(input.toLowerCase()) >= 0;
    },

    handleBookmark() {
      VideoService.updateTaskRecord(this.video.id, {
        starred: !this.video.starred,
      });

      this.$emit('bookMarkVideo', {
        videoName: this.video.fileName,
        updatedObj: { ...this.video, starred: !this.video.starred },
      });
    },

    handleCopyUrl(data) {
      this.$emit('handleCopyUrl', data);
    },
    toggleShowAssignGroupModal() {
      if (this.showAssignGroupModal) {
        this.selectedFile = null;
        this.showAssignGroupModal = false;
        return;
      }
      this.showAssignGroupModal = !this.showAssignGroupModal;
    },

    async validateVideoModal(video) {
      this.toggleShowAssignGroupModal();
      this.selectedFile = video;
    },

    async validateVideo() {
      // this.toggleShowAssignGroupModal();
      let video = this.selectedFile;

      let tasks = await getSortedTask(this.allTasks);
      let payload = {
        task: this.selectedTask,
        task_name: tasks.find((data) => data.id == this.selectedTask).taskName,
        file_name: video.fileName,
        organization: this.organization,
        isUploadedVideo: false,
        group_id: this.selectedValidateGroup,
        time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        device_id: video.device_id,
      };
      await TelemetryService.markVideoValidate(payload);

      this.$emit('handleUpdateVideo', {
        fileName: video.fileName,
        updatedObj: { is_validation_record: !this.video.is_validation_record },
      });
      this.toggleShowAssignGroupModal();
    },
    isThumbnailGenerationInitiated(video) {
      return this.queuedThumbnailGenerations.includes(video.id);
    },

    async handleThumbnailGeneration(video) {
      const [error] = await this.generateThumbnail(video);
      if (error) {
        return;
      }
      this.$emit('updateVideo', {
        videoName: this.video.fileName,
        updatedObj: { ...this.video, isThumbnailExist: true },
      });
      this.queuedThumbnailGenerations = this.queuedThumbnailGenerations.filter(
        (id) => id !== video.id
      );
    },

    async generateThumbnail(record) {
      const payload = {
        id: record.id,
      };
      return await ThumbnailService.generateThumbnail(payload);
    },

    handleError(video) {
      this.$emit('updateVideo', {
        videoName: video.fileName,
        updatedObj: { ...video, isThumbnailExist: false },
      });
    },

    thumbnailUrl(record) {
      const taskId = this.selectedTask;
      if (!taskId || !this.video || !Object.keys(this.video).length) return;
      const thumbnailPath = traceThumbnail(record, taskId, this.organization);
      return getThumbnailUrl(thumbnailPath);
    },

    getObjectLength() {
      return Object.keys(this.video).length;
    },

    async handleVideoPlay() {
      // console.log('inside handle video play');
      // const { cycle_id, taskId } = this.$route.query;
      // console.log('taskID: ', taskId);
      // this.fetchTaskDetailsNew(Number(taskId));
      this.$emit('handlePlay', {
        video: this.video,
        index: this.index,
      });
    },

    updateVideoUrl(videoName, url) {
      this.$emit('updateVideoUrl', videoName, url, this.index);
    },

    getFileName(filename) {
      let name = filename?.slice(0, 30);
      if (filename?.length > 30) name += '...';
      return name;
    },

    getPopupContainer() {
      const elemId = this.isInterestingCycles
        ? 'area1-' + this.index
        : 'area2-' + this.index;
      return document.getElementById(elemId);
    },

    async archiveCycle(video) {
      await TelemetryService.archiveCycle(video.cycle_id);
      this.emitter.emit('getAnalyticsAndTrace', true);
      this.$emit('archivedCycleCount', video.is_good_cycle);
    },

    openTagModal() {
      this.tagsModal = true;
    },

    async fetchOrgTags() {
      this.isLoadingTag = true;
      const res = await httpClient.get('organization/task_record/tags', false);
      this.orgTags = res;
      this.isLoadingTag = false;
    },

    async handleAddTag(tagId) {
      this.loadingTags = true;
      if (!this.selectedVideo.entity_id) {
        const res = await this.createEntity();
        const temp = { ...this.selectedVideo };
        temp['entity_id'] = res.id;
        this.selectedVideo = temp;
        this.updateTaskRecord(
          { entity_id: this.selectedVideo.entity_id },
          this.selectedVideo,
          false
        );
      }

      const tag_res = await this.createTag(tagId);
      tag_res['tag_name'] = this.orgTags.find(
        (ot) => ot.id === tag_res.tag_name
      );

      let selectedTagName = this.tagOptions.find(
        (data) => data.value == tagId
      ).label;
      this.setNewTags({ tag: tag_res.id, tag_value: selectedTagName });
      this.tags = [...this.tags, { id: tag_res.id, name: selectedTagName }];
      this.loadingTags = false;
      this.fetchTags(this.video);
    },

    async handleTagRemove(tagId) {
      this.loadingTags = true;
      const { entity_id } = this.selectedVideo;
      const entityToDelete = this.tags.find((t) => t.name === tagId);
      if (!entityToDelete) return;

      let [res, data] = await TelemetryService.removeTimeStampTags(
        entityToDelete.id
      );

      if (res === 'error') {
        console.log('error occured while removing tag');
      }

      this.loadingTags = false;
    },

    showTagsModal(data) {
      this.selectedVideo = data.taskRecord;
      this.orgTags = data.orgTags;
      this.tags = data.entityTag;
      this.currentSelectedTag = data.entityTag.map((e) => e.name);
      this.tagOptions = data.orgTags.map((el) => ({
        value: el.id,
        label: el.tag_name,
      }));
      this.tagsModal = true;
    },

    async createEntity() {
      const payload = {
        name: this.selectedVideo.fileName,
        organization: this.organization,
      };
      return await VideoService.createEntityForVideo(payload);
    },

    async fetchTags(record, isShowModal = true) {
      if (!record.entity_id && isShowModal) {
        this.showTagsModal({
          taskRecord: record,
          orgTags: this.orgTags || [],
          entityTag: [],
        });
      } else {
        const [error, data] = await TelemetryService.getTimeStampTags(
          record.id
        );
        if (error) {
          this.toast.error('Unable to fetch tags!');
          this.isLoading = false;
          return;
        }
        if (isShowModal) {
          this.showTagsModal({
            taskRecord: record,
            orgTags: this.orgTags || [],
            entityTag: data.map((data) => ({
              id: data.id,
              name: data.tag.name,
            })),
          });
        }
        this.tags = data;
        this.tagsCount = data.length;
      }
    },

    async createTag(tagId) {
      const payload = {
        tag: tagId,
        // entity_id: this.selectedVideo.entity_id,
        added_by: parseInt(this.userId),
        task_record: this.video.id,
        organization: this.organization,
      };

      const [error, data] = await TelemetryService.addTimeStampTags(payload);
      if (error) {
        this.toast.error(`Unable to add ${this.tagIdToNameMap[tag]}!`);
      }

      return data;
      // return await httpClient.post(
      //   `organization/tag_time_stamp_videos_view`,
      //   payload,
      //   false,
      //   false,
      //   false
      // );
    },

    async updateTaskRecord(updatedData, videoToUpdate, alert = true) {
      this.updateVideo(videoToUpdate.fileName, updatedData);
      const [error] = await VideoService.updateTaskRecord(
        videoToUpdate.id,
        updatedData
      );
      if (error && alert) {
        this.toast.success('Error occured while updating the record!');
      }
      if (alert) this.toast.success('Record updated successfully!');
    },

    updateVideo(videoName, updatedObj) {
      if (this.video.fileName === videoName) {
        for (const key in updatedObj) {
          if (updatedObj.hasOwnProperty(key)) {
            this.video[key] = updatedObj[key];
          }
        }
      }
    },
  },
};
</script>

<style scoped>
/* Same surface as the KPI tiles above (RichCardItem). Not clipped, so the
   missed-steps menu can extend past the card. */
.cycle-card {
  display: flex;
  gap: 14px;
  height: 100%;
  padding: 12px;
  background: #ffffff;
  border: 1px solid #eef0f3;
  border-radius: 14px;
  box-shadow: 0 2px 10px rgba(15, 23, 42, 0.04);
  transition: box-shadow 0.15s ease, border-color 0.15s ease;
}
.cycle-card:hover {
  border-color: #e2e6ec;
  box-shadow: 0 8px 22px rgba(15, 23, 42, 0.08);
}

.cycle-thumb {
  position: relative;
  flex: 0 0 clamp(150px, 36%, 224px);
  align-self: flex-start;
  aspect-ratio: 16 / 9;
  border-radius: 10px;
  overflow: hidden;
  background: #0f172a;
}
.cycle-thumb--missing {
  background: #f5f6f8;
  border: 1px dashed #d9dde3;
}
.video-img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.2s ease;
}
.cycle-thumb:hover .video-img {
  transform: scale(1.03);
}
.cycle-thumb .cycle-thumb__missing-img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
  padding: 10px;
  border-radius: 0;
  opacity: 0.75;
}
.cycle-thumb__play {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 40px;
  height: 40px;
  margin: -20px 0 0 -20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.6);
  color: #ffffff;
  font-size: 18px;
  opacity: 0;
  transition: opacity 0.15s ease;
  pointer-events: none;
}
.cycle-thumb:hover .cycle-thumb__play {
  opacity: 1;
}
.cycle-thumb__duration {
  position: absolute;
  right: 6px;
  bottom: 6px;
  padding: 1px 6px;
  border-radius: 4px;
  background: rgba(15, 23, 42, 0.78);
  color: #ffffff;
  font-size: 11px;
  font-weight: 600;
  line-height: 18px;
  font-variant-numeric: tabular-nums;
  pointer-events: none;
}

.cycle-card__body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
  text-align: left;
}
.cycle-card__head {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}
.cycle-card__title {
  min-width: 0;
  font-size: 14px;
  font-weight: 700;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-variant-numeric: tabular-nums;
}
.cycle-card__status {
  margin-right: 0;
  border-radius: 999px;
  font-weight: 600;
  flex-shrink: 0;
}
.cycle-card__head-actions {
  flex-shrink: 0;
}
.cycle-card__bookmark {
  display: inline-flex;
  align-items: center;
  padding: 0 4px;
}
.cycle-card__bookmark :deep(.ant-rate) {
  font-size: 16px;
  line-height: 1;
}

.cycle-card__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 28px;
}
.cycle-meta__label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: #6b7280;
}
.cycle-meta__value {
  margin-top: 2px;
  font-size: 15px;
  font-weight: 600;
  color: #111827;
  line-height: 1.3;
  font-variant-numeric: tabular-nums;
}
.cycle-meta__value--quiet {
  color: #9ca3af;
  font-weight: 500;
}
.cycle-meta__value--alert {
  color: #cf1322;
  font-weight: 700;
}

.cycle-card__toolbar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px 8px;
  margin-top: auto;
  padding-top: 8px;
  border-top: 1px dashed #f0f0f0;
}
.cycle-card__missed-btn {
  padding-left: 0;
  padding-right: 0;
}
.cycle-card__toolbar :deep(.ant-btn-text),
.cycle-card__head-actions :deep(.ant-btn-text) {
  color: #6b7280;
}
.cycle-card__toolbar :deep(.ant-btn-text:hover),
.cycle-card__head-actions :deep(.ant-btn-text:hover) {
  color: #111827;
}
.cycle-card__toolbar :deep(.ant-btn-dangerous.ant-btn-text),
.cycle-card__toolbar :deep(.ant-btn-dangerous.ant-btn-text:hover) {
  color: #ff4d4f;
}
.cycle-card__toolbar-sep {
  display: inline-block;
  width: 1px;
  height: 16px;
  margin: 0 4px;
  background: #e5e7eb;
  vertical-align: middle;
}

/* Stack on phones: full-width thumbnail above the details */
@media (max-width: 575px) {
  .cycle-card {
    flex-direction: column;
  }
  .cycle-thumb {
    flex: 0 0 auto;
    width: 100%;
    align-self: stretch;
  }
}
</style>
